import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Coins, Flame, Gem, Archive, RefreshCw, Search,
  TrendingUp, Scale, Hash, Layers, Filter, Settings,
  FileSpreadsheet, Printer, Download, SlidersHorizontal,
  Calendar, Calculator, RotateCcw, X, ArrowLeft
} from 'lucide-react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { API_URL, API_HOST } from '../config/api';
import useEscapeClose from '../hooks/useEscapeClose';
import { handlePrint } from '../utils/printHelper';
import './StockReport.css';

const IMAGE_HOST = API_HOST;

// Varsayılan sütun ayarları
const defaultColumns = {
  image: true,
  barcode: true,
  name: true,
  category: true,
  brand: false,
  weight: true,
  laboredGram: true,
  stock: true,
  price: true,
  currency: false,
  supplier: false,
  color: false,
  size: false
};

// Ayar katsayıları
const karatMultipliers = {
  '8': 333,
  '9': 375,
  '10': 417,
  '14': 585,
  '18': 750,
  '21': 875,
  '22': 916,
  '24-995': 995,
  '24-999': 999
};

const StockReport = ({ user, selectedStore }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('gold');
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [prices, setPrices] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('stockReportVisibleColumns');
    if (saved) {
      return { ...defaultColumns, ...JSON.parse(saved) };
    }
    return defaultColumns;
  });
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalStock: 0,
    totalWeight: 0,
    categories: {}
  });

  // Sıralama state'leri
  const [sortField, setSortField] = useState(() => {
    const saved = localStorage.getItem('stockReportSortField');
    return saved || null;
  });
  const [sortDirection, setSortDirection] = useState(() => {
    const saved = localStorage.getItem('stockReportSortDirection');
    return saved || 'asc';
  });

  // Sıralama fonksiyonu
  const handleSort = (field) => {
    let newDirection = 'asc';
    if (sortField === field) {
      if (sortDirection === 'asc') {
        newDirection = 'desc';
      } else {
        setSortField(null);
        setSortDirection('asc');
        localStorage.removeItem('stockReportSortField');
        localStorage.removeItem('stockReportSortDirection');
        return;
      }
    }
    setSortField(field);
    setSortDirection(newDirection);
    localStorage.setItem('stockReportSortField', field);
    localStorage.setItem('stockReportSortDirection', newDirection);
  };

  // Tüm sütun tanımları
  const allColumns = [
    { key: 'image', label: 'Görsel' },
    { key: 'barcode', label: 'Barkod' },
    { key: 'name', label: 'Ürün Adı' },
    { key: 'category', label: 'Kategori' },
    { key: 'brand', label: 'Marka' },
    { key: 'weight', label: 'Gramaj' },
    { key: 'laboredGram', label: 'İşçilikli Gram' },
    { key: 'stock', label: 'Stok' },
    { key: 'price', label: 'Fiyat' },
    { key: 'currency', label: 'Para Birimi' },
    { key: 'supplier', label: 'Tedarikçi' },
    { key: 'color', label: 'Renk' },
    { key: 'size', label: 'Ölçü' }
  ];

  // Sütun görünürlüğünü değiştir
  const toggleColumn = (columnKey) => {
    const newColumns = { ...visibleColumns, [columnKey]: !visibleColumns[columnKey] };
    setVisibleColumns(newColumns);
    localStorage.setItem('stockReportVisibleColumns', JSON.stringify(newColumns));
  };

  // Tüm sütunları sıfırla
  const resetColumns = () => {
    setVisibleColumns(defaultColumns);
    localStorage.setItem('stockReportVisibleColumns', JSON.stringify(defaultColumns));
  };

  // Görünen sütun sayısı
  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length;

  // Export durumu
  const [exporting, setExporting] = useState(false);

  // Detaylı filtre
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    name: '',
    barcode: '',
    brand: '',
    supplier: '',
    category: '',
    minPrice: '',
    maxPrice: '',
    minStock: '',
    maxStock: '',
    minWeight: '',
    maxWeight: '',
    stockEntryAfter: '',
    stockEntryBefore: '',
    noStockChangeSince: '',
    hasImage: '',
    ecommerceActive: ''
  });

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [showColumnSettings, () => setShowColumnSettings(false)],
    [showAdvancedFilter, () => setShowAdvancedFilter(false)],
  ]);

  // Metal türleri
  const metalTypes = [
    { id: 'gold', label: 'Altın', icon: Coins, color: '#f59e0b' },
    { id: 'silver', label: 'Gümüş', icon: Coins, color: '#9ca3af' },
    { id: 'steel', label: 'Çelik', icon: Coins, color: '#71717a' },
    { id: 'zippo', label: 'Zippo', icon: Flame, color: '#ef4444' },
    { id: 'aksesuar', label: 'Aksesuar', icon: Gem, color: '#06b6d4' },
    { id: 'markpirsing', label: 'Markalar', icon: Gem, color: '#ec4899' },
    { id: 'sarf', label: 'Sarf', icon: Archive, color: '#8b5cf6' }
  ];

  // Fiyatları getir
  const fetchPrices = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/prices`);
      const data = await response.json();
      setPrices(data);
    } catch (error) {
      console.error('Fiyatlar yüklenemedi:', error);
    }
  }, []);

  // Sayfa yüklendiğinde fiyatları getir
  useEffect(() => {
    fetchPrices();
    // Her 60 saniyede fiyatları güncelle
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, [fetchPrices]);

  // Satış kuru hesapla
  const getSatisRate = (currency) => {
    if (!prices) return 0;
    if (currency === 'GOLD') {
      return parseFloat(prices?.altin_satis) || parseFloat(prices?.altin) || 0;
    } else if (currency === 'SILVER') {
      return parseFloat(prices?.gumus_satis) || parseFloat(prices?.gumus) || 0;
    } else {
      return parseFloat(prices?.[`${currency}_satis`]) || parseFloat(prices?.[currency]) || 0;
    }
  };

  // Altın gram fiyatı
  const getGoldGramPrice = () => getSatisRate('GOLD');

  // Altın fiyat hesaplama
  const calculateGoldPrice = (product) => {
    if (product.pricing_type === 'fixed') {
      return Number(product.sell_price) || 0;
    }
    if (product.metal_type !== 'gold' || !product.weight || !product.gold_karat) {
      return Number(product.sell_price) || 0;
    }
    const goldGramPrice = getGoldGramPrice();
    if (goldGramPrice <= 0) return null;

    const gram = parseFloat(product.weight) || 0;
    const labor = parseFloat(product.labor_cost) || 0;
    const multiplier = karatMultipliers[product.gold_karat] || 585;
    const laboredGram = (multiplier + labor) * gram / 1000;
    return laboredGram * goldGramPrice;
  };

  // Gümüş fiyat hesaplama
  const calculateSilverPrice = (product) => {
    if (product.pricing_type === 'fixed') {
      return Number(product.sell_price) || 0;
    }
    if (product.metal_type !== 'silver' || !product.weight) {
      return Number(product.sell_price) || 0;
    }
    const silverGramPrice = getSatisRate('SILVER');
    const usdSellRate = getSatisRate('USD');
    if (silverGramPrice <= 0 || usdSellRate <= 0) return null;

    const gram = parseFloat(product.weight) || 0;
    const purity = parseInt(product.silver_purity) || 925;
    const profitMargin = parseFloat(product.profit_margin) || 3;
    const laborPerGram = parseFloat(product.labor_cost) || 0;

    const pureGram = (gram * purity) / 1000;
    const silverCost = pureGram * silverGramPrice;
    const laborCostTL = gram * laborPerGram * usdSellRate;
    return (silverCost + laborCostTL) * profitMargin;
  };

  // Gümüş aksesuar fiyat hesaplama
  const calculateSilverAccessoryPrice = (product) => {
    if (product.pricing_type === 'fixed') {
      return Number(product.sell_price) || 0;
    }
    if (!product.weight) {
      return Number(product.sell_price) || 0;
    }
    const silverGramPrice = getSatisRate('SILVER');
    if (silverGramPrice <= 0) return null;

    const gram = parseFloat(product.weight) || 0;
    const purity = parseInt(product.silver_purity) || 925;
    const profitMargin = parseFloat(product.profit_margin) || 3;
    const laborPerGram = parseFloat(product.labor_cost) || 0;

    const pureGram = (gram * purity) / 1000;
    const silverCost = pureGram * silverGramPrice;
    const laborCost = gram * laborPerGram * profitMargin;
    return silverCost + laborCost;
  };

  // Ürün fiyatı hesapla
  const getProductPrice = (product) => {
    if (product.metal_type === 'gold' && product.pricing_type !== 'fixed') {
      return calculateGoldPrice(product);
    } else if (product.metal_type === 'silver' && product.pricing_type !== 'fixed') {
      return calculateSilverPrice(product);
    } else if (product.metal_type === 'aksesuar' && product.category === 'Gümüş Aksesuar' && product.pricing_type !== 'fixed') {
      return calculateSilverAccessoryPrice(product);
    }
    return Number(product.sell_price) || 0;
  };

  // İşçilikli gram hesapla
  const calculateLaboredGram = (product) => {
    if (!product.weight) return null;
    const gram = parseFloat(product.weight) || 0;
    const labor = parseFloat(product.labor_cost) || 0;

    if (product.metal_type === 'gold') {
      // Altın için: (ayar_katsayısı + işçilik) × gram / 1000
      const multiplier = karatMultipliers[product.gold_karat] || 585;
      return ((multiplier + labor) * gram / 1000).toFixed(3);
    } else if (product.metal_type === 'silver' || product.metal_type === 'aksesuar') {
      // Gümüş için: gram × işçilik (işçilik gram başına ise)
      // Veya basitçe: gram + (gram × işçilik oranı)
      const profitMargin = parseFloat(product.profit_margin) || 1;
      return (gram * labor * profitMargin).toFixed(3);
    }
    return null;
  };

  // Ürünleri getir
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: 1,
        limit: 100000,
        metal_type: selectedType
      });

      const response = await fetch(`${API_URL}/products?${params}`);
      const data = await response.json();

      if (data.products) {
        setProducts(data.products);
        calculateStats(data.products);
      }
    } catch (error) {
      console.error('Ürünler yüklenemedi:', error);
      toast.error('Ürünler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [selectedType]);

  // İstatistikleri hesapla
  const calculateStats = (productList) => {
    const categories = {};
    let totalStock = 0;
    let totalWeight = 0;

    productList.forEach(product => {
      const category = product.category || 'Kategorisiz';
      const stock = parseInt(product.stock) || 0;
      const weight = parseFloat(product.weight) || 0;

      if (!categories[category]) {
        categories[category] = {
          count: 0,
          stock: 0,
          weight: 0
        };
      }

      categories[category].count += 1;
      categories[category].stock += stock;
      categories[category].weight += weight * stock; // Toplam ağırlık = birim ağırlık * adet

      totalStock += stock;
      totalWeight += weight * stock;
    });

    setStats({
      totalProducts: productList.length,
      totalStock,
      totalWeight,
      categories
    });
  };

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Arama ve detaylı filtre
  const filteredProducts = products.filter(product => {
    // Metin araması
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = (
        (product.name || '').toLowerCase().includes(search) ||
        (product.barcode || '').toLowerCase().includes(search) ||
        (product.category || '').toLowerCase().includes(search)
      );
      if (!matchesSearch) return false;
    }

    // Detaylı filtreler
    if (advancedFilters.name && !(product.name || '').toLowerCase().includes(advancedFilters.name.toLowerCase())) return false;
    if (advancedFilters.barcode && !(product.barcode || '').toLowerCase().includes(advancedFilters.barcode.toLowerCase())) return false;
    if (advancedFilters.brand && !(product.brand || '').toLowerCase().includes(advancedFilters.brand.toLowerCase())) return false;
    if (advancedFilters.supplier && !(product.supplier || '').toLowerCase().includes(advancedFilters.supplier.toLowerCase())) return false;
    if (advancedFilters.category && !(product.category || '').toLowerCase().includes(advancedFilters.category.toLowerCase())) return false;

    // Fiyat filtreleri
    const price = getProductPrice(product) || 0;
    if (advancedFilters.minPrice && price < parseFloat(advancedFilters.minPrice)) return false;
    if (advancedFilters.maxPrice && price > parseFloat(advancedFilters.maxPrice)) return false;

    // Stok filtreleri
    const stock = parseInt(product.stock) || 0;
    if (advancedFilters.minStock && stock < parseInt(advancedFilters.minStock)) return false;
    if (advancedFilters.maxStock && stock > parseInt(advancedFilters.maxStock)) return false;

    // Ağırlık filtreleri
    const weight = parseFloat(product.weight) || 0;
    if (advancedFilters.minWeight && weight < parseFloat(advancedFilters.minWeight)) return false;
    if (advancedFilters.maxWeight && weight > parseFloat(advancedFilters.maxWeight)) return false;

    // Tarih filtreleri
    if (advancedFilters.stockEntryAfter) {
      const entryDate = product.stock_entry_date ? new Date(product.stock_entry_date) : null;
      const filterDate = new Date(advancedFilters.stockEntryAfter);
      if (!entryDate || entryDate < filterDate) return false;
    }
    if (advancedFilters.stockEntryBefore) {
      const entryDate = product.stock_entry_date ? new Date(product.stock_entry_date) : null;
      const filterDate = new Date(advancedFilters.stockEntryBefore);
      if (!entryDate || entryDate > filterDate) return false;
    }
    if (advancedFilters.noStockChangeSince) {
      const lastChange = product.last_stock_change ? new Date(product.last_stock_change) : null;
      const filterDate = new Date(advancedFilters.noStockChangeSince);
      if (lastChange && lastChange >= filterDate) return false;
    }

    // Resim filtresi
    if (advancedFilters.hasImage === 'yes' && !product.image) return false;
    if (advancedFilters.hasImage === 'no' && product.image) return false;

    // E-ticaret durumu filtresi
    if (advancedFilters.ecommerceActive === 'active' && !product.ecommerce_active) return false;
    if (advancedFilters.ecommerceActive === 'inactive' && product.ecommerce_active) return false;

    return true;
  });

  // Sıralanmış ürünler
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (!sortField) return 0;

    let aValue, bValue;

    switch (sortField) {
      case 'name':
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
        break;
      case 'barcode':
        aValue = a.barcode || '';
        bValue = b.barcode || '';
        break;
      case 'category':
        aValue = (a.category || '').toLowerCase();
        bValue = (b.category || '').toLowerCase();
        break;
      case 'brand':
        aValue = (a.brand || '').toLowerCase();
        bValue = (b.brand || '').toLowerCase();
        break;
      case 'weight':
        aValue = parseFloat(a.weight) || 0;
        bValue = parseFloat(b.weight) || 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      case 'stock':
        aValue = parseInt(a.stock) || 0;
        bValue = parseInt(b.stock) || 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      case 'price':
        aValue = getProductPrice(a) || 0;
        bValue = getProductPrice(b) || 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      case 'supplier':
        aValue = (a.supplier || '').toLowerCase();
        bValue = (b.supplier || '').toLowerCase();
        break;
      default:
        return 0;
    }

    const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Aktif filtre sayısı
  const activeFilterCount = Object.values(advancedFilters).filter(v => v !== '').length;

  // Filtreleri temizle
  const clearFilters = () => {
    setAdvancedFilters({
      name: '',
      barcode: '',
      brand: '',
      supplier: '',
      category: '',
      minPrice: '',
      maxPrice: '',
      minStock: '',
      maxStock: '',
      minWeight: '',
      maxWeight: '',
      stockEntryAfter: '',
      stockEntryBefore: '',
      noStockChangeSince: '',
      hasImage: '',
      ecommerceActive: ''
    });
  };

  // Tür etiketi
  const getTypeLabel = () => {
    const type = metalTypes.find(t => t.id === selectedType);
    return type?.label || selectedType;
  };

  // Excel çıktısı oluştur
  const generateExcel = async () => {
    setExporting(true);
    try {
      const typeLabel = getTypeLabel();
      const now = new Date();
      const dateStr = now.toLocaleDateString('tr-TR');
      const timeStr = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

      // Kategori özet verileri
      const categoryData = Object.entries(stats.categories)
        .sort((a, b) => b[1].stock - a[1].stock)
        .map(([category, data]) => ({
          'Kategori': category,
          'Ürün Çeşidi': data.count,
          'Toplam Adet': data.stock,
          'Toplam Ağırlık (gr)': data.weight.toFixed(2)
        }));

      // Toplam satırı ekle
      categoryData.push({
        'Kategori': 'TOPLAM',
        'Ürün Çeşidi': stats.totalProducts,
        'Toplam Adet': stats.totalStock,
        'Toplam Ağırlık (gr)': stats.totalWeight.toFixed(2)
      });

      // Sütun eşleştirmeleri (image hariç)
      const columnMap = {
        barcode: { key: 'barcode', label: 'Barkod', getValue: (p) => p.barcode || '' },
        name: { key: 'name', label: 'Ürün Adı', getValue: (p) => p.name || '' },
        category: { key: 'category', label: 'Kategori', getValue: (p) => p.category || '' },
        brand: { key: 'brand', label: 'Marka', getValue: (p) => p.brand || '' },
        weight: { key: 'weight', label: 'Gramaj', getValue: (p) => p.weight ? `${p.weight} gr` : '' },
        laboredGram: { key: 'laboredGram', label: 'İşçilikli Gram', getValue: (p) => calculateLaboredGram(p) || '' },
        stock: { key: 'stock', label: 'Stok', getValue: (p) => p.stock || 0 },
        price: { key: 'price', label: 'Fiyat', getValue: (p) => {
          const calculatedPrice = getProductPrice(p);
          return calculatedPrice ? Number(calculatedPrice).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        }},
        currency: { key: 'currency', label: 'Para Birimi', getValue: (p) => p.currency || 'TRY' },
        supplier: { key: 'supplier', label: 'Tedarikçi', getValue: (p) => p.supplier || '' },
        color: { key: 'color', label: 'Renk', getValue: (p) => p.color || '' },
        size: { key: 'size', label: 'Ölçü', getValue: (p) => p.size || '' }
      };

      // Görünen sütunlara göre ürün verilerini oluştur (image hariç)
      const productData = sortedProducts.map(p => {
        const row = {};
        Object.keys(columnMap).forEach(colKey => {
          if (visibleColumns[colKey]) {
            row[columnMap[colKey].label] = columnMap[colKey].getValue(p);
          }
        });
        return row;
      });

      // Workbook oluştur
      const wb = XLSX.utils.book_new();

      // Kategori özet sayfası
      const categorySheetWithHeader = XLSX.utils.json_to_sheet(categoryData, { origin: 'A4' });
      XLSX.utils.sheet_add_aoa(categorySheetWithHeader, [
        [`${typeLabel} Stok Raporu - Kategori Özeti`],
        [`Tarih: ${dateStr} ${timeStr}`],
        []
      ], { origin: 'A1' });
      XLSX.utils.book_append_sheet(wb, categorySheetWithHeader, 'Kategori Özeti');

      // Ürün listesi sayfası
      const productSheetWithHeader = XLSX.utils.json_to_sheet(productData, { origin: 'A4' });
      XLSX.utils.sheet_add_aoa(productSheetWithHeader, [
        [`${typeLabel} Stok Raporu - Ürün Listesi`],
        [`Tarih: ${dateStr} ${timeStr} | Toplam: ${sortedProducts.length} ürün`],
        []
      ], { origin: 'A1' });
      XLSX.utils.book_append_sheet(wb, productSheetWithHeader, 'Ürün Listesi');

      // Dosyayı indir
      const fileName = `Stok_Raporu_${typeLabel}_${now.toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.success('Excel raporu oluşturuldu!');
    } catch (error) {
      console.error('Excel oluşturma hatası:', error);
      toast.error('Excel oluşturulamadı!');
    } finally {
      setExporting(false);
    }
  };

  // PDF çıktısı oluştur (yazdır)
  const generatePDF = async () => {
    setExporting(true);
    try {
      const typeLabel = getTypeLabel();
      const now = new Date();
      const dateStr = now.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
      const timeStr = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

      // Kategori satırları
      const categoryRows = Object.entries(stats.categories)
        .sort((a, b) => b[1].stock - a[1].stock)
        .map(([category, data]) => `
          <tr>
            <td>${category}</td>
            <td style="text-align: center;">${data.count}</td>
            <td style="text-align: center;">${data.stock}</td>
            <td style="text-align: right;">${data.weight.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gr</td>
          </tr>
        `).join('');

      // PDF sütun tanımları (image hariç, görünür olanlara göre)
      const pdfColumns = [];
      if (visibleColumns.barcode) pdfColumns.push({ key: 'barcode', label: 'Barkod', align: 'left', getValue: (p) => p.barcode || '-', style: 'font-family: monospace; font-size: 11px;' });
      if (visibleColumns.name) pdfColumns.push({ key: 'name', label: 'Ürün Adı', align: 'left', getValue: (p) => p.name || '-' });
      if (visibleColumns.category) pdfColumns.push({ key: 'category', label: 'Kategori', align: 'left', getValue: (p) => p.category || '-' });
      if (visibleColumns.brand) pdfColumns.push({ key: 'brand', label: 'Marka', align: 'left', getValue: (p) => p.brand || '-' });
      if (visibleColumns.weight) pdfColumns.push({ key: 'weight', label: 'Gramaj', align: 'right', getValue: (p) => p.weight ? p.weight + ' gr' : '-' });
      if (visibleColumns.laboredGram) pdfColumns.push({ key: 'laboredGram', label: 'İşçilikli Gram', align: 'right', getValue: (p) => calculateLaboredGram(p) || '-' });
      if (visibleColumns.stock) pdfColumns.push({ key: 'stock', label: 'Stok', align: 'center', getValue: (p) => p.stock || 0, getStyle: (p) => `font-weight: bold; color: ${p.stock <= 0 ? '#dc2626' : p.stock <= 3 ? '#d97706' : '#16a34a'};` });
      if (visibleColumns.price) pdfColumns.push({ key: 'price', label: 'Fiyat', align: 'right', getValue: (p) => {
        const calculatedPrice = getProductPrice(p);
        return calculatedPrice ? Number(calculatedPrice).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + (p.currency || 'TRY') : '-';
      }});
      if (visibleColumns.currency) pdfColumns.push({ key: 'currency', label: 'Para Birimi', align: 'center', getValue: (p) => p.currency || 'TRY' });
      if (visibleColumns.supplier) pdfColumns.push({ key: 'supplier', label: 'Tedarikçi', align: 'left', getValue: (p) => p.supplier || '-' });
      if (visibleColumns.color) pdfColumns.push({ key: 'color', label: 'Renk', align: 'left', getValue: (p) => p.color || '-' });
      if (visibleColumns.size) pdfColumns.push({ key: 'size', label: 'Ölçü', align: 'left', getValue: (p) => p.size || '-' });

      // Ürün başlık satırları
      const productHeaders = pdfColumns.map(col =>
        `<th style="text-align: ${col.align};">${col.label}</th>`
      ).join('');

      // Ürün satırları
      const productRows = sortedProducts.map(p => {
        const cells = pdfColumns.map(col => {
          const style = col.getStyle ? col.getStyle(p) : (col.style || '');
          return `<td style="text-align: ${col.align}; ${style}">${col.getValue(p)}</td>`;
        }).join('');
        return `<tr>${cells}</tr>`;
      }).join('');

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${typeLabel} Stok Raporu - ${dateStr}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              padding: 30px;
              color: #1e293b;
              font-size: 12px;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              padding-bottom: 20px;
              border-bottom: 3px solid #00bfa6;
            }
            .header h1 {
              font-size: 24px;
              color: #00bfa6;
              margin-bottom: 8px;
            }
            .header p { color: #64748b; font-size: 14px; }
            .section { margin-bottom: 30px; }
            .section-title {
              font-size: 16px;
              font-weight: 700;
              color: #334155;
              margin-bottom: 15px;
              padding: 10px 15px;
              background: linear-gradient(135deg, rgba(0, 191, 166, 0.1) 0%, rgba(0, 150, 136, 0.1) 100%);
              border-radius: 8px;
              border-left: 4px solid #00bfa6;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            th {
              background: linear-gradient(135deg, #00bfa6 0%, #009688 100%);
              color: white;
              padding: 12px 10px;
              text-align: left;
              font-weight: 600;
              font-size: 11px;
              text-transform: uppercase;
            }
            td {
              padding: 10px;
              border-bottom: 1px solid #e2e8f0;
            }
            tr:nth-child(even) { background: #f8fafc; }
            tr:hover { background: rgba(0, 191, 166, 0.08); }
            .summary-row {
              background: linear-gradient(135deg, rgba(0, 191, 166, 0.15) 0%, rgba(0, 150, 136, 0.15) 100%) !important;
              font-weight: 700;
            }
            .summary-row td { border-top: 2px solid #00bfa6; }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 15px;
              margin-bottom: 25px;
            }
            .stat-box {
              background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
              padding: 20px;
              border-radius: 12px;
              text-align: center;
              border: 1px solid #e2e8f0;
            }
            .stat-value {
              font-size: 28px;
              font-weight: 800;
              color: #00bfa6;
            }
            .stat-label {
              font-size: 12px;
              color: #64748b;
              margin-top: 5px;
            }
            .footer {
              margin-top: 30px;
              padding-top: 15px;
              border-top: 1px solid #e2e8f0;
              text-align: center;
              color: #94a3b8;
              font-size: 11px;
            }
            @media print {
              body { padding: 15px; }
              .section { page-break-inside: avoid; }
              table { font-size: 10px; }
              th, td { padding: 6px 8px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${typeLabel} Stok Raporu</h1>
            <p>${dateStr} - ${timeStr}</p>
          </div>

          <div class="section">
            <div class="stats-grid">
              <div class="stat-box">
                <div class="stat-value">${stats.totalProducts.toLocaleString('tr-TR')}</div>
                <div class="stat-label">Ürün Çeşidi</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">${stats.totalStock.toLocaleString('tr-TR')}</div>
                <div class="stat-label">Toplam Adet</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">${stats.totalWeight.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gr</div>
                <div class="stat-label">Toplam Ağırlık</div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Kategori Bazlı Dağılım</div>
            <table>
              <thead>
                <tr>
                  <th>Kategori</th>
                  <th style="text-align: center;">Ürün Çeşidi</th>
                  <th style="text-align: center;">Toplam Adet</th>
                  <th style="text-align: right;">Toplam Ağırlık</th>
                </tr>
              </thead>
              <tbody>
                ${categoryRows}
                <tr class="summary-row">
                  <td><strong>TOPLAM</strong></td>
                  <td style="text-align: center;"><strong>${stats.totalProducts}</strong></td>
                  <td style="text-align: center;"><strong>${stats.totalStock}</strong></td>
                  <td style="text-align: right;"><strong>${stats.totalWeight.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gr</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="section">
            <div class="section-title">Ürün Listesi (${sortedProducts.length} ürün)</div>
            <table>
              <thead>
                <tr>
                  ${productHeaders}
                </tr>
              </thead>
              <tbody>
                ${productRows}
              </tbody>
            </table>
          </div>

          <div class="footer">
            <p>Bu rapor Akik Gümüş Stok Yönetim Sistemi tarafından oluşturulmuştur.</p>
          </div>

          <script>
            window.onload = function() {
              var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
              setTimeout(function() {
                window.print();
                if (directPrint) { setTimeout(function() { window.close(); }, 500); }
              }, 300);
            }
          </script>
        </body>
        </html>
      `;

      await handlePrint(html, 'label', { windowOptions: '' });

      toast.success('PDF raporu oluşturuldu! Yazdırma penceresi açıldı.');
    } catch (error) {
      console.error('PDF oluşturma hatası:', error);
      toast.error('PDF oluşturulamadı!');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="stock-report-page">
      {/* Üst Menü */}
      <header className="page-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/products')}>
            <ArrowLeft size={16} /> Ürün Yönetimi
          </button>
          <div className="page-title-wrapper">
            <h1><Package size={24} /> Stok Raporu</h1>
          </div>
        </div>
        <div className="header-center">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
            className="header-logo"
          />
        </div>
        <div className="header-right">
          <button className="refresh-btn" onClick={fetchProducts} disabled={loading} title="Yenile">
            <RefreshCw size={20} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </header>

      {/* İçerik Alanı */}
      <div className="content-wrapper">
        {/* Metal Tür Filtreleri */}
        <div className="type-filters">
        {metalTypes.map(type => (
          <button
            key={type.id}
            className={`type-filter-btn ${selectedType === type.id ? 'active' : ''}`}
            onClick={() => setSelectedType(type.id)}
            style={{ '--type-color': type.color }}
          >
            <type.icon size={20} />
            <span>{type.label}</span>
          </button>
        ))}
      </div>

      {/* Özet Rapor */}
      <div className="stats-panel">
        <h2><TrendingUp size={20} /> {getTypeLabel()} Stok Özeti</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon"><Layers size={24} /></div>
            <div className="stat-content">
              <span className="stat-value">{stats.totalProducts.toLocaleString('tr-TR')}</span>
              <span className="stat-label">Ürün Çeşidi</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><Hash size={24} /></div>
            <div className="stat-content">
              <span className="stat-value">{stats.totalStock.toLocaleString('tr-TR')}</span>
              <span className="stat-label">Toplam Adet</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><Scale size={24} /></div>
            <div className="stat-content">
              <span className="stat-value">
                {stats.totalWeight.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gr
              </span>
              <span className="stat-label">Toplam Ağırlık</span>
            </div>
          </div>
        </div>

        {/* Kategori Bazlı Özet */}
        <div className="category-stats">
          <h3><Filter size={18} /> Kategori Bazlı Dağılım</h3>
          <div className="category-grid">
            {Object.entries(stats.categories)
              .sort((a, b) => b[1].stock - a[1].stock)
              .map(([category, data]) => (
                <div key={category} className="category-stat-card">
                  <span className="category-name">{category}</span>
                  <div className="category-details">
                    <span>{data.count} çeşit</span>
                    <span>{data.stock} adet</span>
                    <span>
                      {data.weight.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gr
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Export Butonları */}
        <div className="export-actions">
          <button className="export-btn excel-btn" onClick={generateExcel} disabled={loading || exporting}>
            <FileSpreadsheet size={18} />
            Excel İndir
          </button>
          <button className="export-btn pdf-btn" onClick={generatePDF} disabled={loading || exporting}>
            <Printer size={18} />
            PDF / Yazdır
          </button>
        </div>
      </div>

      {/* Arama ve Sütun Ayarları */}
      <div className="search-and-settings">
        <div className="search-bar">
          <Search size={20} />
          <input
            type="text"
            placeholder="Ürün ara (ad, barkod, kategori)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          className={`filter-btn ${activeFilterCount > 0 ? 'active' : ''}`}
          onClick={() => setShowAdvancedFilter(true)}
        >
          <SlidersHorizontal size={16} />
          Detaylı Filtre
          {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
        </button>
        <div className="column-settings-wrapper">
          <button
            className="column-settings-btn"
            onClick={() => setShowColumnSettings(!showColumnSettings)}
          >
            <Settings size={16} /> Sütunlar
          </button>
          {showColumnSettings && (
            <div className="column-settings-dropdown">
              <div className="column-settings-header">
                <span>Görünür Sütunlar</span>
                <button className="reset-columns-btn" onClick={resetColumns}>Sıfırla</button>
              </div>
              <div className="column-settings-list">
                {allColumns.map(col => (
                  <label key={col.key} className="column-toggle">
                    <input
                      type="checkbox"
                      checked={visibleColumns[col.key]}
                      onChange={() => toggleColumn(col.key)}
                    />
                    <span>{col.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ürün Tablosu */}
      <div className="products-table-container">
        {loading ? (
          <div className="loading-state">
            <RefreshCw size={32} className="spinning" />
            <span>Ürünler yükleniyor...</span>
          </div>
        ) : (
          <table className="products-table">
            <thead>
              <tr>
                {visibleColumns.image && <th>Görsel</th>}
                {visibleColumns.barcode && (
                  <th className={`sortable ${sortField === 'barcode' ? 'sorted' : ''}`} onClick={() => handleSort('barcode')}>
                    Barkod
                    {sortField === 'barcode' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                )}
                {visibleColumns.name && (
                  <th className={`sortable ${sortField === 'name' ? 'sorted' : ''}`} onClick={() => handleSort('name')}>
                    Ürün Adı
                    {sortField === 'name' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                )}
                {visibleColumns.category && (
                  <th className={`sortable ${sortField === 'category' ? 'sorted' : ''}`} onClick={() => handleSort('category')}>
                    Kategori
                    {sortField === 'category' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                )}
                {visibleColumns.brand && (
                  <th className={`sortable ${sortField === 'brand' ? 'sorted' : ''}`} onClick={() => handleSort('brand')}>
                    Marka
                    {sortField === 'brand' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                )}
                {visibleColumns.weight && (
                  <th className={`sortable ${sortField === 'weight' ? 'sorted' : ''}`} onClick={() => handleSort('weight')}>
                    Gramaj
                    {sortField === 'weight' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                )}
                {visibleColumns.laboredGram && (
                  <th className={`sortable ${sortField === 'laboredGram' ? 'sorted' : ''}`} onClick={() => handleSort('laboredGram')}>
                    İşçilikli Gram
                    {sortField === 'laboredGram' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                )}
                {visibleColumns.stock && (
                  <th className={`sortable ${sortField === 'stock' ? 'sorted' : ''}`} onClick={() => handleSort('stock')}>
                    Stok
                    {sortField === 'stock' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                )}
                {visibleColumns.price && (
                  <th className={`sortable ${sortField === 'price' ? 'sorted' : ''}`} onClick={() => handleSort('price')}>
                    Fiyat
                    {sortField === 'price' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                )}
                {visibleColumns.currency && <th>Para Birimi</th>}
                {visibleColumns.supplier && (
                  <th className={`sortable ${sortField === 'supplier' ? 'sorted' : ''}`} onClick={() => handleSort('supplier')}>
                    Tedarikçi
                    {sortField === 'supplier' && <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                )}
                {visibleColumns.color && <th>Renk</th>}
                {visibleColumns.size && <th>Ölçü</th>}
              </tr>
            </thead>
            <tbody>
              {sortedProducts.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumnCount} className="empty-state">
                    {searchTerm ? 'Arama sonucu bulunamadı' : 'Bu türde ürün bulunamadı'}
                  </td>
                </tr>
              ) : (
                sortedProducts.map(product => (
                  <tr key={product.id}>
                    {visibleColumns.image && (
                      <td className="image-cell">
                        {product.image ? (
                          <img src={`${IMAGE_HOST}${product.image}`} alt={product.name} />
                        ) : (
                          <div className="no-image"><Package size={20} /></div>
                        )}
                      </td>
                    )}
                    {visibleColumns.barcode && <td className="barcode-cell">{product.barcode || '-'}</td>}
                    {visibleColumns.name && <td className="name-cell">{product.name}</td>}
                    {visibleColumns.category && <td className="category-cell">{product.category || '-'}</td>}
                    {visibleColumns.brand && <td className="brand-cell">{product.brand || '-'}</td>}
                    {visibleColumns.weight && <td className="weight-cell">{product.weight ? `${product.weight} gr` : '-'}</td>}
                    {visibleColumns.laboredGram && <td className="labored-gram-cell">{calculateLaboredGram(product) || '-'}</td>}
                    {visibleColumns.stock && (
                      <td className="stock-cell">
                        {product.stock || 0}
                      </td>
                    )}
                    {visibleColumns.price && (
                      <td className="price-cell">
                        {(() => {
                          const calculatedPrice = getProductPrice(product);
                          return calculatedPrice
                            ? `${Number(calculatedPrice).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${product.currency || 'TRY'}`
                            : '-';
                        })()}
                      </td>
                    )}
                    {visibleColumns.currency && <td className="currency-cell">{product.currency || 'TRY'}</td>}
                    {visibleColumns.supplier && <td className="supplier-cell">{product.supplier || '-'}</td>}
                    {visibleColumns.color && <td className="color-cell">{product.color || '-'}</td>}
                    {visibleColumns.size && <td className="size-cell">{product.size || '-'}</td>}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Alt Bilgi */}
        <div className="table-footer">
          <span>Toplam {sortedProducts.length} ürün listeleniyor</span>
        </div>
      </div>

      {/* Detaylı Filtre Modal */}
      {showAdvancedFilter && (
        <div className="modal-overlay" onClick={() => setShowAdvancedFilter(false)}>
          <div className="modal advanced-filter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><SlidersHorizontal size={20} /> Detaylı Filtre</h2>
              <button className="close-btn" onClick={() => setShowAdvancedFilter(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="filter-grid">
                {/* Metin Filtreleri */}
                <div className="filter-section">
                  <h3><Search size={16} /> Metin Araması</h3>
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Ürün Adı</label>
                      <input
                        type="text"
                        placeholder="Ürün adı ara..."
                        value={advancedFilters.name}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, name: e.target.value })}
                      />
                    </div>
                    <div className="filter-group">
                      <label>Barkod</label>
                      <input
                        type="text"
                        placeholder="Barkod ara..."
                        value={advancedFilters.barcode}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, barcode: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Marka</label>
                      <input
                        type="text"
                        placeholder="Marka ara..."
                        value={advancedFilters.brand}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, brand: e.target.value })}
                      />
                    </div>
                    <div className="filter-group">
                      <label>Tedarikçi</label>
                      <input
                        type="text"
                        placeholder="Tedarikçi ara..."
                        value={advancedFilters.supplier}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, supplier: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="filter-row">
                    <div className="filter-group full-width">
                      <label>Kategori</label>
                      <input
                        type="text"
                        placeholder="Kategori ara..."
                        value={advancedFilters.category}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, category: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Sayısal Filtreler */}
                <div className="filter-section">
                  <h3><Calculator size={16} /> Sayısal Filtreler</h3>
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Min. Fiyat (₺)</label>
                      <input
                        type="number"
                        placeholder=""
                        min="0"
                        value={advancedFilters.minPrice}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, minPrice: e.target.value })}
                      />
                    </div>
                    <div className="filter-group">
                      <label>Max. Fiyat (₺)</label>
                      <input
                        type="number"
                        placeholder="∞"
                        min="0"
                        value={advancedFilters.maxPrice}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, maxPrice: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Min. Stok</label>
                      <input
                        type="number"
                        placeholder=""
                        min="0"
                        value={advancedFilters.minStock}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, minStock: e.target.value })}
                      />
                    </div>
                    <div className="filter-group">
                      <label>Max. Stok</label>
                      <input
                        type="number"
                        placeholder="∞"
                        min="0"
                        value={advancedFilters.maxStock}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, maxStock: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Min. Gramaj (gr)</label>
                      <input
                        type="number"
                        placeholder=""
                        min="0"
                        step="0.01"
                        value={advancedFilters.minWeight}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, minWeight: e.target.value })}
                      />
                    </div>
                    <div className="filter-group">
                      <label>Max. Gramaj (gr)</label>
                      <input
                        type="number"
                        placeholder="∞"
                        min="0"
                        step="0.01"
                        value={advancedFilters.maxWeight}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, maxWeight: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Tarih Filtreleri */}
                <div className="filter-section">
                  <h3><Calendar size={16} /> Tarih Filtreleri</h3>
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Stok Giriş (Sonrası)</label>
                      <input
                        type="date"
                        value={advancedFilters.stockEntryAfter}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, stockEntryAfter: e.target.value })}
                      />
                    </div>
                    <div className="filter-group">
                      <label>Stok Giriş (Öncesi)</label>
                      <input
                        type="date"
                        value={advancedFilters.stockEntryBefore}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, stockEntryBefore: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="filter-row">
                    <div className="filter-group full-width">
                      <label>Stok Hareketi Olmayan (Bu Tarihten Beri)</label>
                      <input
                        type="date"
                        value={advancedFilters.noStockChangeSince}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, noStockChangeSince: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Durum Filtreleri */}
                <div className="filter-section">
                  <h3><Filter size={16} /> Durum Filtreleri</h3>
                  <div className="filter-row">
                    <div className="filter-group">
                      <label>Ürün Resmi</label>
                      <select
                        value={advancedFilters.hasImage}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, hasImage: e.target.value })}
                      >
                        <option value="">Tümü</option>
                        <option value="yes">Resimli Ürünler</option>
                        <option value="no">Resimsiz Ürünler</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>E-Ticaret Durumu</label>
                      <select
                        value={advancedFilters.ecommerceActive}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, ecommerceActive: e.target.value })}
                      >
                        <option value="">Tümü</option>
                        <option value="active">E-Ticaret Aktif</option>
                        <option value="inactive">E-Ticaret Pasif</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={clearFilters}>
                <RotateCcw size={16} /> Temizle
              </button>
              <button className="btn-apply" onClick={() => setShowAdvancedFilter(false)}>
                <Search size={16} /> Uygula
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockReport;
